import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/DocumentManage.module.css";
import { v4 as uuidv4 } from "uuid";
import {
  FiUpload,
  FiFile,
  FiDownload,
  FiTrash2,
  FiEye,
  FiX,
  FiRefreshCw,
  FiSearch,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
} from "react-icons/fi";

const DocumentManagement = () => {
  // State management
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Refs
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const modalBodyRef = useRef(null);
  const modalFooterRef = useRef(null);

  // Fetch documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Apply search filtering
  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm]);

  const filterDocuments = () => {
    let filtered = [...documents];
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((doc) => {
        const fileName = (doc.original_filename || "").toLowerCase();
        return fileName.includes(term);
      });
    }
    setFilteredDocuments(filtered);
  };

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(
        `${config.API_URL}/management/get-all-documents`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch documents");
      const data = await response.json();

      if (Array.isArray(data.response)) {
        const normalizedData = data.response.map((doc) => ({
          id: doc.document_id,
          original_filename: doc.original_filename || "Untitled",
          file_size: doc.file_size || 0,
          uploaded_date: doc.uploaded_date || "",
          file_type: doc.file_type || "",
          uploaded_by: doc.uploaded_by || "",
        }));
        setDocuments(normalizedData);
        setFilteredDocuments(normalizedData);
      } else {
        setDocuments([]);
        setFilteredDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const validFiles = files.filter((file) => {
      if (file.type !== "application/pdf") {
        setError("Only PDF files are allowed");
        return false;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError("File size must be less than 50MB");
        return false;
      }
      return true;
    });

    const filesWithMetadata = validFiles.map((file) => ({
      file,
      id: uuidv4(),
      name: file.name,
      size: file.size,
      category: "general",
    }));

    setSelectedFiles((prev) => [...prev, ...filesWithMetadata]);
    setError(null);
  };

  const removeSelectedFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const uploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select files to upload");
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      for (const fileData of selectedFiles) {
        const formData = new FormData();
        formData.append("file", fileData.file);
        formData.append("category", fileData.category);

        setUploadProgress((prev) => ({ ...prev, [fileData.id]: 0 }));

        const response = await fetch(
          `${config.API_URL}/management/upload-document`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to upload ${fileData.name}`);
        }

        setUploadProgress((prev) => ({ ...prev, [fileData.id]: 100 }));
      }

      setSuccess(`Successfully uploaded ${selectedFiles.length} document(s)`);
      setSelectedFiles([]);
      setShowUploadModal(false);
      await fetchDocuments();
    } catch (error) {
      console.error("Error uploading documents:", error);
      setError(error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  const downloadDocument = async (documentId, fileName) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/view-document/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download document");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      setError(error.message);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/delete-document/${documentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete document");

      setSuccess("Document deleted successfully");
      await fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      setError(error.message);
    }
  };

  const handlePreviewDocument = async (document) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/view-document/${document.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to load document preview");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      setPreviewDoc({
        ...document,
        previewUrl: url,
        original_filename: document.original_filename,
      });
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Error previewing document:", error);
      setError(error.message);
    }
  };

  const closePreviewModal = () => {
    if (previewDoc?.previewUrl) {
      window.URL.revokeObjectURL(previewDoc.previewUrl);
    }
    setPreviewDoc(null);
    setShowPreviewModal(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    
    // Format as UTC time
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'  // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
    }

    return date.toLocaleString('en-US', options).replace(',', '');
  };

  // Auto-hide success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading documents...</p>
      </div>
    );
  }

  return (
    <div className={styles.documentManagement}>
      {/* Header */}
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Document Management</h1>
          <div className={styles.headerActions}>
            <button
              className={styles.refreshButton}
              onClick={fetchDocuments}
              disabled={isLoading}
            >
              <FiRefreshCw className={isLoading ? styles.refreshing : ""} />
            </button>
            <button
              className={styles.uploadButton}
              onClick={() => setShowUploadModal(true)}
            >
              <FiUpload />
              Upload Documents
            </button>
          </div>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      {/* Notifications */}
      {error && (
        <div className={`${styles.notification} ${styles.error}`}>
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <FiX />
          </button>
        </div>
      )}

      {success && (
        <div className={`${styles.notification} ${styles.success}`}>
          <FiCheckCircle />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>
            <FiX />
          </button>
        </div>
      )}

      {/* Documents Grid */}
      <div className={styles.documentsContainer}>
        {filteredDocuments.length > 0 ? (
          <div className={styles.documentsGrid}>
            {filteredDocuments.map((document) => (
              <div key={document.id} className={styles.documentCard}>
                <div className={styles.documentIcon}>
                  <FiFileText />
                </div>

                <div className={styles.documentInfo}>
                  <h3 className={styles.documentTitle}>
                    {document.original_filename || "Untitled Document"}
                  </h3>
                  <div className={styles.documentMeta}>
                    <span className={styles.documentSize}>
                      {formatFileSize(document.file_size)}
                    </span>
                    <span className={styles.documentDate}>
                      {formatDateTime(document.uploaded_date)}
                    </span>
                  </div>
                </div>

                <div className={styles.documentActions}>
                  <button
                    className={`${styles.actionButton} ${styles.preview}`}
                    onClick={() => handlePreviewDocument(document)}
                    title="Preview"
                  >
                    <FiEye />
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.download}`}
                    onClick={() =>
                      downloadDocument(document.id, document.original_filename)
                    }
                    title="Download"
                  >
                    <FiDownload />
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.delete}`}
                    onClick={() => deleteDocument(document.id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.noDocuments}>
            <FiFile size={64} />
            <h3>No documents found</h3>
            <p>Upload your first document to get started</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className={styles.uploadModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Upload Documents</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowUploadModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div
              className={styles.modalBody}
              ref={modalBodyRef}
              style={{
                paddingBottom: "80px", // Space for the fixed footer
              }}
            >
              {/* File Upload Area */}
              <div
                ref={dragRef}
                className={`${styles.uploadArea} ${
                  dragActive ? styles.dragActive : ""
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FiUpload size={48} className={styles.uploadIcon} />
                <h3>Drag & drop PDF files here</h3>
                <p>or click to select files</p>
                <small>Maximum file size: 50MB</small>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFileInput}
                  style={{ display: "none" }}
                />
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className={styles.fileList}>
                  <h3>Selected Files ({selectedFiles.length})</h3>
                  {selectedFiles.map((fileData) => (
                    <div key={fileData.id} className={styles.fileItem}>
                      <div className={styles.fileInfo}>
                        <FiFile />
                        <div className={styles.fileDetails}>
                          <div className={styles.fileName}>{fileData.name}</div>
                          <div className={styles.fileSize}>
                            {formatFileSize(fileData.size)}
                          </div>
                        </div>
                      </div>

                      {uploadProgress[fileData.id] !== undefined && (
                        <div className={styles.uploadProgress}>
                          <div className={styles.progressBar}>
                            <div
                              className={styles.progressFill}
                              style={{
                                width: `${uploadProgress[fileData.id]}%`,
                              }}
                            />
                          </div>
                          <div className={styles.progressText}>
                            <span>Uploading...</span>
                            <span>{uploadProgress[fileData.id]}%</span>
                          </div>
                        </div>
                      )}

                      <button
                        className={styles.removeFileButton}
                        onClick={() => removeSelectedFile(fileData.id)}
                        disabled={isUploading}
                      >
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className={styles.modalFooter}
              ref={modalFooterRef}
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                backgroundColor: "white",
                borderTop: "1px solid #e2e8f0",
                padding: "1rem",
              }}
            >
              <button
                className={styles.cancelButton}
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className={styles.confirmButton}
                onClick={uploadDocuments}
                disabled={selectedFiles.length === 0 || isUploading}
              >
                {isUploading
                  ? "Uploading..."
                  : `Upload ${selectedFiles.length} File(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewDoc && (
        <div className={styles.modalOverlay} onClick={closePreviewModal}>
          <div
            className={styles.previewModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>{previewDoc.original_filename}</h2>
              <button
                className={styles.closeButton}
                onClick={closePreviewModal}
              >
                <FiX />
              </button>
            </div>

            <div
              className={styles.modalBody}
              style={{
                paddingBottom: "80px", // Space for the fixed footer
              }}
            >
              <div className={styles.previewContent}>
                <iframe
                  src={previewDoc.previewUrl}
                  title="Document Preview"
                  width="100%"
                  height="600px"
                  style={{ border: "none", borderRadius: "8px" }}
                />
              </div>
            </div>

            <div
              className={styles.modalFooter}
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                backgroundColor: "white",
                borderTop: "1px solid #e2e8f0",
                padding: "1rem",
              }}
            >
              <button
                className={`${styles.actionButton} ${styles.download2}`}
                onClick={() =>
                  downloadDocument(previewDoc.id, previewDoc.original_filename)
                }
              >
                <FiDownload />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;